import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { Product, ProductCreate, ProductUpdate, PaginatedResponse } from '../../types/product'
import type { RootState } from '../index'

export const productApi = createApi({
  reducerPath: 'productApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${import.meta.env.VITE_API_BASE_URL || ''}/api/admin`,
    prepareHeaders: (headers, { getState }) => {
      // Get token from Redux state
      const token = (getState() as RootState).auth.token
      console.log('Product API - Token from state:', token ? token.substring(0, 20) + '...' : 'NO TOKEN')
      console.log('Product API - Setting Authorization header')
      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
        console.log('Product API - Authorization header set!')
      } else {
        console.error('Product API - NO TOKEN FOUND!')
      }
      return headers
    },
  }),
  tagTypes: ['Product'],
  endpoints: (builder) => ({
    getProducts: builder.query<PaginatedResponse<Product>, {
      page?: number
      per_page?: number
      category_id?: string
      status?: string
      search?: string
      price_min?: number
      price_max?: number
      stock_min?: number
      stock_max?: number
    }>({
      query: (params) => ({
        url: '/products',
        params,
      }),
      providesTags: ['Product'],
    }),
    getProduct: builder.query<Product, string>({
      query: (id) => `/products/${id}`,
      providesTags: ['Product'],
    }),
    createProduct: builder.mutation<Product, ProductCreate>({
      query: (data) => ({
        url: '/products',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),
    updateProduct: builder.mutation<Product, { id: string; data: ProductUpdate }>({
      query: ({ id, data }) => ({
        url: `/products/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),
    deleteProduct: builder.mutation<void, string>({
      query: (id) => ({
        url: `/products/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Product'],
    }),
    bulkImport: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: '/products/bulk-import',
        method: 'POST',
        body: formData,
      }),
      invalidatesTags: ['Product'],
    }),
    bulkUpdate: builder.mutation<{
      total_products: number
      successful: number
      failed: number
      results: Array<{
        product_id: string
        success: boolean
        error?: string
      }>
    }, {
      product_ids: string[]
      operation: 'update_category' | 'update_pricing' | 'update_stock' | 'update_status'
      data: any
    }>({
      query: (data) => ({
        url: '/products/bulk-update',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Product'],
    }),
    bulkExport: builder.mutation<Blob, { category_id?: string; status?: string }>({
      query: (params) => ({
        url: '/products/bulk-export',
        method: 'GET',
        params,
        responseHandler: (response) => response.blob(),
      }),
    }),
  }),
})

export const {
  useGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
  useBulkImportMutation,
  useBulkUpdateMutation,
  useBulkExportMutation,
} = productApi
